import classnames from "classnames";
import * as React from "react";
import { Link } from "react-router-dom";

import { Link as LinkType } from "features/common/routing/utils";

import "./WithLink.scss";

type Props = {
  linkClassName?: string;
  to?: LinkType | null | undefined;
  target?: string;
  onClick?: () => void;
  replace?: boolean;
  disabled?: boolean;
  isExternal?: boolean;
  loading?: boolean;
};

type Options = {
  dataTest?: string;
};

function WithLink<P extends Record<string, any>>(
  WrappedComponent: React.ComponentType<P>,
  { dataTest }: Options = {}
): React.ComponentType<P & Props> {
  return function WithLink(props: P & Props) {
    const {
      to,
      target,
      onClick,
      replace,
      linkClassName,
      isExternal,
      ...otherProps
    } = props;

    const linkIsDisabled = props.disabled || props.loading;

    if (!to) {
      if (process.env.NODE_ENV === "development" && !linkIsDisabled) {
        const name = WrappedComponent.displayName || WrappedComponent.name;
        console.log(
          `${name} is being wrapped in withLink but no "to" prop is being passed`
        );
      }

      return (
        // @ts-ignore
        <WrappedComponent
          {...otherProps}
          onClick={onClick}
          disabled={props.disabled}
        />
      );
    }

    if (isExternal) {
      return (
        <a
          className={classnames("WithLink__link", linkClassName, {
            WithLink__disabled: linkIsDisabled
          })}
          href={to as any}
          target="_blank"
          onClick={onClick}
          rel="nofollow noopener noreferrer"
          data-test={dataTest}
        >
          {/* @ts-ignore */}
          <WrappedComponent {...otherProps} />
        </a>
      );
    }

    return (
      <Link
        className={classnames("WithLink__link", linkClassName, {
          WithLink__disabled: linkIsDisabled
        })}
        to={to}
        target={target}
        onClick={onClick}
        replace={replace}
        data-test={dataTest}
      >
        {/* @ts-ignore */}
        <WrappedComponent {...otherProps} />
      </Link>
    );
  };
}

export default WithLink;
